import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { parseUrlParams } from './lib/url-utils'
import VirtualStagingWidget from './components/virtual-staging-widget'

// Lazy load the iframe test page since it's only needed for development
const IframeTestPage = lazy(() => import('./components/iframe-test-page').then(module => ({ default: module.IframeTestPage })))

// Query param interface
interface WidgetParams {
  user_id?: string;
  client_key?: string;
  user_image_url?: string;
  room_type?: string;
  style?: string;
  auto_declutter?: boolean;
  extra?: Record<string, any>;
}

// Simple loading fallback for the iframe test page
const TestPageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="text-xl">Loading test environment...</div>
  </div>
)

function WidgetRoutes() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parsedParams = parseUrlParams(searchParams);
  const [params, setParams] = useState<WidgetParams>({
    ...(parsedParams as WidgetParams),
  });

  // Set default overflow behavior for the widget
  useEffect(() => {
    // Add style to document head
    const style = document.createElement('style');
    const cssRule = 'html, body { overflow: auto; }';

    style.textContent = cssRule;
    document.head.appendChild(style);

    // Clean up function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Routes>
      <Route path="/" element={<VirtualStagingWidget {...params} />} />
      <Route path="/embed/:id" element={<VirtualStagingWidget {...params} />} />

      {/* Hidden route for iframe testing */}
      <Route path="/_test/iframe" element={
        <Suspense fallback={<TestPageLoading />}>
          <IframeTestPage />
        </Suspense>
      } />

      <Route path="*" element={<div>Not found</div>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <WidgetRoutes />
      <Toaster position="top-center" />
    </BrowserRouter>
  );
}